import cx from "classnames";
import { useEffect, useRef } from "react";
import { Breadcrumb } from "@/components/Breadcrumb";
import ContainerBoundary from "@/components/common/ContainerBoundary";
import ErrorBoundary from "@/components/empty/ErrorBoundary";
import ScoreBoardContainer from "@/components/scoreboard/ScoreboardContainer";
import MoreNews from "@/components/sport/MoreNews";
import SportArticlesContainer from "@/components/sport/SportArticlesContainer";
import { LEAGUE_PAGE_VIEW } from "@/constants/amplitude";
import { Article, GenericSport } from "@/graphql/gql/graphql";
import { SingleSportBets } from "@/pages/[slug]";
import { BreadcrumbTrail } from "@/types/breadcrumbs";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useRecentArticles from "@/utils/hooks/useRecentArticles";
import { TopLevelCategoryJsonLd } from "@/utils/seo";
import BetDisplay from "../author/featuredBet/BetDisplay";
import styles from "./sportContainer.module.scss";

interface Props {
  sport: Partial<GenericSport>;
  featuredArticles: Article[];
  featuredBets?: SingleSportBets;
  trail: BreadcrumbTrail;
}

const SportContainer = ({
  sport,
  featuredArticles,
  featuredBets,
  trail,
}: Props) => {
  const {
    articles: moreArticles,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
  } = useRecentArticles({
    limit: 12,
    sportName: sport.name,
    hideDeprioritizedContent: true,
  });

  const { setPage, logAmplitudeEvent, isActive } = useAmplitude();
  const titleRef = useRef("");
  useEffect(() => {
    if (
      titleRef.current !== sport.seo?.title &&
      titleRef.current !== sport.name &&
      isActive
    ) {
      titleRef.current = sport.seo?.title ?? sport.name ?? "";
      setPage("league");
      logAmplitudeEvent(
        LEAGUE_PAGE_VIEW,
        { Competition: sport.name ?? "" },
        { page: "league" }
      );
    }
  }, [setPage, sport.seo?.title, sport.name, logAmplitudeEvent, isActive]);

  let title = `${sport.name} Home`;
  if (sport.homePageTitle?.trim()) {
    title = sport.homePageTitle;
  }
  const { featuredBetHeading, featuredBetPage, featuredBetFooter } =
    featuredBets ?? {};
  return (
    <ErrorBoundary>
      <ContainerBoundary legacy>
        <TopLevelCategoryJsonLd sport={sport} articles={moreArticles} />
        <Breadcrumb trail={trail} />
        <h1 className={styles.heading}>{title}</h1>
        {featuredBetHeading?.props?.[0]?.marketStatus === "OPEN" ? (
          <div className={styles.shadowContainer}>
            <BetDisplay
              bet={featuredBetHeading}
              eventName={featuredBetHeading?.props?.[0]?.marketName ?? ""}
              name="featuredBetHeading"
              sportName={sport.name}
              showTitle={false}
            />
          </div>
        ) : null}
        <SportArticlesContainer articles={featuredArticles} />
        <div className={styles.lowerArea}>
          <section className={styles.section}>
            {featuredBetPage?.props?.[0]?.marketStatus === "OPEN" ? (
              <div className={styles.shadowContainer}>
                <BetDisplay
                  bet={featuredBetPage}
                  eventName={featuredBetPage?.props?.[0]?.marketName ?? ""}
                  name="featuredBetPage"
                  sportName={sport.name}
                  showTitle={false}
                />
              </div>
            ) : null}
            {sport.hasLiveScoring && (
              <div className={styles.shadowContainer}>
                <ScoreBoardContainer sport={sport.name} />
              </div>
            )}

            <h2 className={cx(styles.heading, styles.recent)}>
              {`Recent ${sport.name} News`}
            </h2>
            {moreArticles && (
              <MoreNews
                hasMore={!!hasNextPage}
                loadMore={fetchNextPage}
                articles={moreArticles}
                origin="recent_stories"
                isLoading={isLoading || isFetching}
              />
            )}
            {featuredBetFooter?.props?.[0]?.marketStatus === "OPEN" ? (
              <div className={styles.shadowContainer}>
                <BetDisplay
                  bet={featuredBetFooter}
                  eventName={featuredBetFooter?.props?.[0]?.marketName ?? ""}
                  name="featuredBetFooter"
                  sportName={sport.name}
                  showTitle={false}
                />
              </div>
            ) : null}
          </section>
        </div>
      </ContainerBoundary>
    </ErrorBoundary>
  );
};
export default SportContainer;
